import { useState } from 'react'
import { toast } from 'sonner'
import {
  useBackupTargets,
  useBackupJobs,
  useRunBackup,
  useUpdateTarget,
  useCreateTarget,
  type DbTarget,
} from '@/lib/backups'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { bytes, relative } from '@/lib/format'

function Jobs({ targetId }: { targetId: string }) {
  const { data, isLoading } = useBackupJobs(targetId)
  if (isLoading) return <Skeleton className="h-16" />
  if (!data || data.length === 0)
    return <p className="text-sm text-muted-foreground">No runs yet.</p>

  const last6 = data.slice(0, 6)
  const okCount = data.filter((j) => j.status === 'ok').length
  const failCount = data.filter((j) => j.status === 'failed').length
  // Consecutive failures at the head of the list (most recent first).
  let consecutiveFails = 0
  for (const j of data) {
    if (j.status === 'failed') consecutiveFails++
    else break
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        <span>
          {okCount} ok · {failCount} failed over last {data.length}
        </span>
        {consecutiveFails >= 2 && (
          <Badge variant="destructive" className="text-[10px]">
            {consecutiveFails} consecutive failures
          </Badge>
        )}
      </div>
      <div className="divide-y text-xs">
        {last6.map((j) => {
          const when = j.finished_at
            ? new Date(j.finished_at).toLocaleString()
            : j.started_at
              ? `running since ${new Date(j.started_at).toLocaleTimeString()}`
              : 'queued'
          return (
            <div key={j.id} className="py-2 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant={
                    j.status === 'ok'
                      ? 'outline'
                      : j.status === 'failed'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {j.status}
                </Badge>
                {j.kind === 'manual' && (
                  <span className="text-[10px] text-muted-foreground border border-border rounded px-1">
                    manual
                  </span>
                )}
                <span className="text-muted-foreground">{when}</span>
                {j.size_bytes != null && (
                  <span className="ml-auto text-muted-foreground">
                    {bytes(j.size_bytes)}
                  </span>
                )}
                {j.duration_s != null && (
                  <span className="text-muted-foreground">
                    · {j.duration_s.toFixed(1)}s
                  </span>
                )}
              </div>
              {j.path && (
                <div
                  className="font-mono text-[10px] text-muted-foreground truncate"
                  title={j.path}
                >
                  {j.path}
                </div>
              )}
              {j.status === 'failed' && j.error_text && (
                <div
                  className="text-[11px] text-destructive/80 line-clamp-3"
                  title={j.error_text}
                >
                  {j.error_text}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TargetRow({ t }: { t: DbTarget }) {
  const run = useRunBackup()
  const upd = useUpdateTarget()
  const isSqlite = t.engine === 'sqlite'
  const [cron, setCron] = useState(t.schedule_cron)
  const [dest, setDest] = useState(t.destination_path)
  const [dsn, setDsn] = useState('')
  const [src, setSrc] = useState(t.source_path ?? '')

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t.service_name ?? t.id}</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="uppercase text-[10px]">{t.engine}</Badge>
          <Badge variant={t.enabled ? 'outline' : 'secondary'}>{t.enabled ? 'enabled' : 'disabled'}</Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                await run.mutateAsync(t.id)
                toast.success('Backup started')
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Run failed')
              }
            }}
            disabled={run.isPending}
          >
            Run now
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="space-y-1">
            <Label>Schedule (cron)</Label>
            <Input value={cron} onChange={(e) => setCron(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Destination path</Label>
            <Input value={dest} onChange={(e) => setDest(e.target.value)} />
          </div>
          {isSqlite ? (
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                Source file (path)
                {t.dsn_set ? (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[10px]">set</Badge>
                ) : (
                  <Badge variant="outline" className="border-destructive/50 text-destructive text-[10px]">not configured</Badge>
                )}
              </Label>
              <Input
                placeholder="/data/codeur/prod/missions.db"
                value={src}
                onChange={(e) => setSrc(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                DSN (write-only)
                {t.dsn_set ? (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[10px]">set</Badge>
                ) : (
                  <Badge variant="outline" className="border-destructive/50 text-destructive text-[10px]">not configured</Badge>
                )}
              </Label>
              <Input
                type="password"
                placeholder={t.dsn_set ? 'Already set, leave empty to keep' : 'postgres://user:pass@host:5432/db'}
                value={dsn}
                onChange={(e) => setDsn(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={async () => {
              try {
                await upd.mutateAsync({
                  id: t.id,
                  body: {
                    schedule_cron: cron !== t.schedule_cron ? cron : undefined,
                    destination_path: dest !== t.destination_path ? dest : undefined,
                    dsn_plain: !isSqlite && dsn ? dsn : undefined,
                    source_path: isSqlite && src !== (t.source_path ?? '') ? src : undefined,
                  },
                })
                setDsn('')
                toast.success('Target updated')
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Update failed')
              }
            }}
            disabled={upd.isPending}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant={t.enabled ? 'ghost' : 'outline'}
            onClick={() => void upd.mutateAsync({ id: t.id, body: { enabled: !t.enabled } })}
          >
            {t.enabled ? 'Disable' : 'Enable'}
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            {t.last_run_at ? `last: ${relative(t.last_run_at)}` : 'never run'}
            {' · '}
            {t.next_run_at ? `next: ${new Date(t.next_run_at).toLocaleString()}` : 'not scheduled'}
          </div>
        </div>
        <Jobs targetId={t.id} />
      </CardContent>
    </Card>
  )
}

function AddSqliteTarget() {
  const create = useCreateTarget()
  const [src, setSrc] = useState('')
  const [svc, setSvc] = useState('')
  const [cron, setCron] = useState('0 3 * * *')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Add a SQLite backup target</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          For file-based databases Zeus can't auto-discover (no DB container). The file must be
          on a path mounted into the Zeus api/scheduler (e.g. under <code>/data</code>).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="space-y-1">
            <Label>Source file</Label>
            <Input placeholder="/data/codeur/prod/missions.db" value={src} onChange={(e) => setSrc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Service to link (optional)</Label>
            <Input placeholder="codeur_app" value={svc} onChange={(e) => setSvc(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Schedule (cron)</Label>
            <Input value={cron} onChange={(e) => setCron(e.target.value)} />
          </div>
        </div>
        <Button
          size="sm"
          disabled={create.isPending || !src.trim()}
          onClick={async () => {
            try {
              await create.mutateAsync({
                engine: 'sqlite',
                source_path: src.trim(),
                service_name: svc.trim() || undefined,
                schedule_cron: cron.trim() || undefined,
              })
              setSrc('')
              setSvc('')
              toast.success('SQLite target created')
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Create failed')
            }
          }}
        >
          {create.isPending ? 'Creating…' : 'Create target'}
        </Button>
      </CardContent>
    </Card>
  )
}

export function BackupsPage() {
  const { data, isLoading } = useBackupTargets()
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Backups</h1>
        <p className="text-muted-foreground">
          Databases backed up on their own schedule — Postgres/Neo4j auto-discovered in your Swarm,
          plus manually-added SQLite files.
        </p>
      </div>
      {isLoading && <Skeleton className="h-40" />}
      {!isLoading && (!data || data.length === 0) && (
        <Card>
          <CardContent className="pt-6 text-muted-foreground">
            No databases discovered yet.
          </CardContent>
        </Card>
      )}
      <div className="space-y-4">
        {data?.map((t) => <TargetRow key={t.id} t={t} />)}
      </div>
      <AddSqliteTarget />
    </div>
  )
}
